import Button from '../../components/ui/Button';
import Trophy from '../../assets/images/Trophy.png';
import goal from '../../assets/images/Goal.png';
import { useNavigate } from 'react-router-dom';
import getGameType from '../../utils/games';
import getGameImage from '../../utils/getGameImage';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
const GRADIENTS = {
  blue: `conic-gradient(
    from 0deg,
    #0E64D1 0deg 40deg,
    #ffea4f 40deg 90deg,
    #0E64D1 90deg 130deg,
    #ffea4f 130deg 180deg,
    #0E64D1 180deg 220deg,
    #ffea4f 220deg 270deg,
    #0E64D1 270deg 310deg,
    #ffea4f 310deg 360deg
  )`,
  green: `conic-gradient(
    from 0deg,
    #0ED173 0deg 40deg,
    white 40deg 90deg,
    #0ED173 90deg 130deg,
    white 130deg 180deg,
    #0ED173 180deg 220deg,
    white 220deg 270deg,
    #0ED173 270deg 310deg,
    white 310deg 360deg
  )`,
  red: `conic-gradient(
    from 0deg,
    #D92000 0deg 40deg,
    white 40deg 90deg,
    #D92000 90deg 130deg,
    white 130deg 180deg,
    #D92000 180deg 220deg,
    white 220deg 270deg,
    #D92000 270deg 310deg,
    white 310deg 360deg
  )`,
};

export default function GameResultCard({
  game,
  combinations,
  drawDate,
  drawTime,
  win,
  hit,
  gameSettings,
  gameId,
  id,
  refreshResults,
}) {
  const navigate = useNavigate();
  const gameType = getGameType(
    gameSettings?.find((game) => game.id === gameId)?.otherSettings?.image
  );
  const GameImage = getGameImage(gameType);
  const gameName = gameSettings?.find((game) => game.id === gameId)?.name;
  const { tokens, user } = useAuth();

  // Format date and time
  const formattedDate = dayjs(drawDate).format('ddd, MMM D, YYYY');
  const formattedTime = dayjs(`2000-01-01 ${drawTime}`).format('h:mm A');

  // Transform the combinations string into an array of digit objects
  const formatCombinations = (combinations) => {
    // Handle combinations with dashes (e.g., "29-04")
    const parts = combinations.split('-');
    let digits = [];

    parts.forEach((part, partIndex) => {
      const partDigits = part.split('').map((num, index) => {
        const position = partIndex * part.length + index;
        const styles = {
          0: { color: '[#0E64D1]', gradient: GRADIENTS.blue },
          1: { color: 'green-400', gradient: GRADIENTS.green },
          2: { color: 'red-600', gradient: GRADIENTS.red },
        };

        return {
          num,
          color: styles[position % 3].color,
          gredient: styles[position % 3].gradient,
        };
      });

      digits = [...digits, ...partDigits];

      // Add dash if there are more parts
      if (partIndex < parts.length - 1) {
        digits.push({
          num: '-',
          color: 'gray-400',
          gredient: null,
        });
      }
    });

    return digits;
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount);
  };

  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        gamesApi.deleteGameResult(tokens.access.token, id).then((res) => {
          if (res.status === 204) {
            toast.success('Game result deleted successfully');
            refreshResults();
          } else {
            toast.error('Failed to delete game result');
          }
        });
      }
    });
  };

  const numbers = formatCombinations(combinations);

  return (
    <div className='bg-white'>
      <div className='flex items-start gap-2.5 border p-2.5 mt-2.5 rounded-md'>
        <img
          src={GameImage}
          alt={gameName}
          width={62}
          height={62}
          className='rounded-lg'
        />
        <div className='flex-1'>
          <div className='flex items-center justify-between'>
            <h3 className='font-bold text-base'>{gameName}</h3>
            <div className='flex gap-2'>
              <Button
                className='bg-[#0E64D1] text-white text-xs px-2 p-1.5 rounded-[4px]'
                text='View Details'
                onClick={() => navigate(`/game-results/${id}`)}
              />
              {user.roleId === 1 && (
                <Button
                  className='bg-[#D92000] text-white text-xs px-2 p-1.5 rounded-[4px]'
                  text='Delete'
                  onClick={handleDelete}
                />
              )}
            </div>
          </div>
          <div className='flex items-center gap-2 mt-2'>
            <div className={`flex flex-wrap items-center ${gameName === 'PICK 3' ? 'gap-0.5' : 'gap-1'}`}>
              {numbers.map((digit, i) =>
                digit.num === '-' ? (
                  <span key={i} className='text-gray-400 mx-1'>
                    -
                  </span>
                ) : (
                  <div
                    key={i}
                    className='relative flex items-center justify-center w-6 h-6'
                  >
                    <div
                      className='absolute inset-0 rounded-full'
                      style={{
                        transform: 'rotate(25deg)',
                        background: digit.gredient,
                      }}
                    />
                    <div
                      className={`${gameName === 'PICK 3' ? 'w-[20px] h-[20px] text-xs' : 'w-[22px] h-[22px] text-sm'} relative flex items-center justify-center rounded-full bg-${digit.color} text-white font-medium`}
                    >
                      {digit.num}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4 mt-4'>
            <div>
              <div className='text-[10px] text-gray-600'>Draw Date</div>
              <div className='font-semibold text-xs'>{formattedDate}</div>
            </div>
            <div>
              <div className='text-[10px] text-gray-600'>Draw Time</div>
              <div className='font-semibold text-xs'>{formattedTime}</div>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4 mt-4'>
            <div
              className='rounded-lg flex justify-between'
              style={{
                background: 'linear-gradient(180deg, #D92000 0%, #B01A00 100%)',
              }}
            >
              <div className='text-white p-2.5 self-center'>
                <div className='text-xs font-normal max-xs:text-[10px]'>
                  Total Hits
                </div>
                <div className='font-semibold text-xl max-xs:text-[18px]'>
                  {formatMoney(hit)}
                </div>
              </div>
              <div className='self-end'>
                <img
                  src={goal}
                  alt='Total Hits'
                  className='w-[62px] max-xs:w-[40px]'
                />
              </div>
            </div>
            <div
              className='rounded-lg flex justify-between'
              style={{
                background: 'linear-gradient(180deg, #FEDA50 0%, #AB431A 100%)',
              }}
            >
              <div className='text-white p-2.5 self-center'>
                <div className='text-xs font-normal max-xs:text-[10px]'>
                  Total Win
                </div>
                <div className='font-bold text-xl max-xs:text-[18px]'>
                  {formatMoney(win)}
                </div>
              </div>
              <div className='self-end relative'>
                <img
                  src={Trophy}
                  alt='Total Win'
                  className='w-[62px] max-xs:w-[40px]'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
